import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Tab, Tabs, Button, Table } from "react-bootstrap";
import { FaShoppingCart, FaChartBar, FaChartLine, FaExclamationTriangle, FaListUl, FaUtensils, FaCreditCard } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api, API_BASE_URL } from "../../config/api";
import SharedOrderDetailModal from "../orders/SharedOrderDetailModal";
import { updateOrderStatusApi } from '../menu/service/api_service';

export default function Dashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [data, setData] = useState({
    summary: { basicProfitToday: 0, avgDailyNetProfit: 0, todoOrders: 0, unpaidOrdersCount: 0, unpaidOrdersAmount: 0 },
    salesGraph: [],
    ordersList: { todo: [], unpaid: [] },
    lowStockAlerts: []
  });

  const [loading, setLoading] = useState(true);

  // Selected Order Modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchDashboardData = async () => {
    try {
      const res = await api.get('/api/dashboard/overview', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch dashboard overview", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Listen for real-time order updates
    const sseUrl = `${API_BASE_URL}/api/orders/stream?token=${token}`;
    const evtSource = new EventSource(sseUrl);

    evtSource.addEventListener('ORDER_CREATED', (event) => {
      fetchDashboardData();
    });
    evtSource.addEventListener('ORDER_UPDATED', (event) => {
      fetchDashboardData();
    });

    evtSource.onerror = (err) => {
      console.error("EventSource failed.", err);
      evtSource.close();
    };

    return () => {
      evtSource.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleSaveOrder = async (updatedDraft, file = null) => {
    try {
      const formData = new FormData();
      formData.append("orderData", JSON.stringify(updatedDraft));
      if (file) {
        formData.append("slip", file);
      }
      await updateOrderStatusApi(token, updatedDraft.id, formData);
      setShowModal(false);
      setSelectedOrder(null);
      // Wait a tiny bit then refresh dashboard data
      setTimeout(fetchDashboardData, 300);
    } catch (err) {
      console.error(err);
      alert("Failed to update order");
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return <Badge bg="secondary">Pending</Badge>;
      case 'cooking': return <Badge bg="warning" text="dark">Cooking</Badge>;
      case 'ready': return <Badge bg="primary">Ready to Deliver</Badge>;
      case 'completed': return <Badge bg="success">Completed</Badge>;
      case 'cancelled': return <Badge bg="danger">Cancelled</Badge>;
      default: return <Badge bg="dark">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Loading Dashboard...</div>;
  }

  const { summary, salesGraph, ordersList, lowStockAlerts } = data;

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 fw-bold" style={{ color: "#1e3c72" }}>Dashboard</h2>
        <div className="text-muted small">{new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
      </div>

      {/* Row 1: Summary Cards */}
      <Row className="g-3 mb-4">
        <Col md={3} sm={6}>
          <Card className="shadow-sm border-0 h-100 border-start border-success border-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small fw-bold text-uppercase mb-1">กำไรเบื้องต้น (วันนี้)</div>
                  <h3 className="mb-0 fw-bold text-success">฿{summary.basicProfitToday.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits:2})}</h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded-circle text-success">
                  <FaChartBar size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="shadow-sm border-0 h-100 border-start border-info border-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small fw-bold text-uppercase mb-1">กำไร/ขาดทุนสุทธิ (เฉลี่ยรายวัน)</div>
                  <h3 className="mb-0 fw-bold text-info">฿{summary.avgDailyNetProfit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits:2})}</h3>
                </div>
                <div className="bg-info bg-opacity-10 p-3 rounded-circle text-info">
                  <FaChartLine size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="shadow-sm border-0 h-100 border-start border-warning border-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small fw-bold text-uppercase mb-1">ออเดอร์ต้องทำ</div>
                  <h3 className="mb-0 fw-bold text-warning">{summary.todoOrders} <span className="fs-6">คิว</span></h3>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded-circle text-warning">
                  <FaUtensils size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="shadow-sm border-0 h-100 border-start border-danger border-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small fw-bold text-uppercase mb-1">ออเดอร์ยังไม่จ่ายเงิน</div>
                  <h3 className="mb-0 fw-bold text-danger">
                    {summary.unpaidOrdersCount} <span className="fs-6 text-muted">บิล (฿{summary.unpaidOrdersAmount.toLocaleString()})</span>
                  </h3>
                </div>
                <div className="bg-danger bg-opacity-10 p-3 rounded-circle text-danger">
                  <FaCreditCard size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Row 1.5: Quick Shortcut */}
      <div className="mb-4">
        <Button 
          size="lg" 
          className="w-100 py-3 fw-bold shadow d-flex justify-content-center align-items-center gap-2" 
          style={{ backgroundColor: "#1e3c72", border: "none", fontSize: "1.2rem", borderRadius: "12px" }}
          onClick={() => navigate('/admin/order')}
        >
          <FaShoppingCart size={24} /> สร้างออเดอร์ (POS)
        </Button>
      </div>

      {/* Row 2: Sales Graph */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white border-0 pt-4 pb-0">
          <h5 className="fw-bold text-secondary mb-0">กราฟยอดขายวันนี้ (แบ่งตามชั่วโมง)</h5>
        </Card.Header>
        <Card.Body style={{ height: "300px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={salesGraph} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="hour" axisLine={false} tickLine={false} tickMargin={10} />
              <YAxis axisLine={false} tickLine={false} tickFormatter={(val) => `฿${val}`} />
              <Tooltip formatter={(value) => [`฿${value}`, "Sales"]} labelFormatter={(label) => `Time: ${label}`} />
              <Line type="monotone" dataKey="sales" stroke="#1e3c72" strokeWidth={3} dot={{ r: 4, fill: '#1e3c72' }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {/* Row 3: Lists and Alerts */}
      <Row className="g-4">
        <Col lg={8}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white border-0 pt-4 pb-0">
              <h5 className="fw-bold text-secondary mb-0"><FaListUl className="me-2"/> รายการออเดอร์ที่ต้องจัดการ</h5>
            </Card.Header>
            <Card.Body>
              <Tabs defaultActiveKey="todo" className="mb-3">
                <Tab eventKey="todo" title={`ต้องทำ (${ordersList.todo.length})`}>
                  <div className="table-responsive">
                    <Table hover align="middle">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>ลูกค้า</th>
                          <th>สถานะ</th>
                          <th>ยอดเงิน</th>
                          <th>เวลา</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordersList.todo.length === 0 ? (
                          <tr><td colSpan="5" className="text-center py-4 text-muted">ไม่มีออเดอร์ค้างทำ</td></tr>
                        ) : (
                          ordersList.todo.map(o => (
                            <tr key={o.id} style={{cursor:'pointer'}} onClick={() => handleOrderClick(o)}>
                              <td>#{o.id}</td>
                              <td>{o.recipient_name}</td>
                              <td>{getStatusBadge(o.status)}</td>
                              <td>฿{Number(o.total_price).toLocaleString()}</td>
                              <td className="text-muted small">{new Date(o.created_at).toLocaleTimeString('th-TH')}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Tab>
                <Tab eventKey="unpaid" title={`ยังไม่จ่าย (${ordersList.unpaid.length})`}>
                  <div className="table-responsive">
                    <Table hover align="middle">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>ลูกค้า</th>
                          <th>ช่องทาง</th>
                          <th>ยอดเงิน</th>
                          <th>เวลา</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordersList.unpaid.length === 0 ? (
                          <tr><td colSpan="5" className="text-center py-4 text-muted">ไม่มีออเดอร์ค้างจ่าย</td></tr>
                        ) : (
                          ordersList.unpaid.map(o => (
                            <tr key={o.id} style={{cursor:'pointer'}} onClick={() => handleOrderClick(o)}>
                              <td>#{o.id}</td>
                              <td>{o.recipient_name}</td>
                              <td><Badge bg="secondary">{o.payment_method}</Badge></td>
                              <td className="text-danger fw-bold">฿{Number(o.total_price).toLocaleString()}</td>
                              <td className="text-muted small">{new Date(o.created_at).toLocaleTimeString('th-TH')}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white border-0 pt-4 pb-0">
              <h5 className="fw-bold text-danger mb-0"><FaExclamationTriangle className="me-2"/> แจ้งเตือนของใกล้หมด</h5>
            </Card.Header>
            <Card.Body>
              {lowStockAlerts.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaExclamationTriangle size={48} className="text-light mb-3" />
                  <p>สต๊อกวัตถุดิบปกติ ไม่มีรายการใกล้หมด</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {lowStockAlerts.map(item => (
                    <div key={item.id} className="p-3 bg-danger bg-opacity-10 border border-danger border-opacity-25 rounded d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-bold text-dark">{item.name}</div>
                        <div className="text-danger small">Min: {item.min_stock} {item.unit}</div>
                      </div>
                      <div className="fs-5 fw-bold text-danger">
                        {item.stock_quantity} <span className="fs-6 text-muted">{item.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <SharedOrderDetailModal
        show={showModal}
        onHide={() => setShowModal(false)}
        order={selectedOrder}
        onSaveOrder={handleSaveOrder}
        readOnly={false}
      />
    </Container>
  );
}
